import { logError } from '../../shared/utils/logger';
import { BASE_URL, fetchWithAuth } from './client';

/**
 * Notes API Module
 * Handles all note-related API calls for the Notes feature
 */

/**
 * Fetch all notes for the current user
 * GET /api/notes
 * Query params: filter, tag, search, pinned, archived, page, limit
 */
export async function fetchNotes(params = {}) {
    const queryParams = new URLSearchParams();

    if (params.filter) queryParams.append('filter', params.filter);
    if (params.tag) queryParams.append('tag', params.tag);
    if (params.search) queryParams.append('search', params.search);
    if (params.pinned !== undefined) queryParams.append('pinned', params.pinned);
    if (params.archived !== undefined) queryParams.append('archived', params.archived);
    if (params.visibility) queryParams.append('visibility', params.visibility);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.caseId) queryParams.append('case_id', params.caseId);
    if (params.linked) queryParams.append('linked', 'true');

    const queryString = queryParams.toString();
    const url = `${BASE_URL}/api/notes${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch notes failed:", response.status, errorText);
        throw new Error("Failed to fetch notes. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Fetch a single note by ID
 * GET /api/notes/{note_id}
 */
export async function fetchNoteById(noteId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/notes/${encodeURIComponent(noteId)}`, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch note failed:", response.status, errorText);

        if (response.status === 404) {
            throw new Error("Note not found.");
        }
        throw new Error("Failed to fetch note. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Create a new note
 * POST /api/notes
 * Body: { content, visibility, pinned, case_id?, attachments? }
 */
export async function createNote(payload) {
    const response = await fetchWithAuth(`${BASE_URL}/api/notes`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            accept: "application/json"
        },
        body: JSON.stringify({
            content: payload.content,
            visibility: payload.visibility || 'PRIVATE',
            pinned: payload.pinned || false,
            case_id: payload.caseId || null,
            attachments: payload.attachments || []
        })
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Create note failed:", response.status, errorText);
        throw new Error("Failed to create note. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Update an existing note
 * PATCH /api/notes/{note_id}
 * Body: { content?, visibility?, pinned?, archived? }
 */
export async function updateNote(noteId, updates) {
    const payload = { ...updates };

    // Handle linking/unlinking if caseId is provided
    if (payload.caseId !== undefined || payload.case_id !== undefined) {
        const caseId = payload.caseId || payload.case_id;
        try {
            if (caseId) {
                await linkNoteToCase(noteId, caseId);
            } else {
                await unlinkNoteFromCase(noteId);
            }
        } catch (error) {
            console.error("Failed to update note link:", error);
            // We continue to update the content even if linking fails?
            // Or throw? Throwing is safer to alert the user.
            throw error;
        }

        // Remove caseId/case_id from payload as we handled it separately
        delete payload.caseId;
        delete payload.case_id;
    }

    // If there are no other updates, return the updated note
    if (Object.keys(payload).length === 0) {
        return fetchNoteById(noteId);
    }

    const response = await fetchWithAuth(`${BASE_URL}/api/notes/${encodeURIComponent(noteId)}`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            accept: "application/json"
        },
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Update note failed:", response.status, errorText);

        if (response.status === 404) {
            throw new Error("Note not found.");
        }
        throw new Error("Failed to update note. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Delete a note
 * DELETE /api/notes/{note_id}
 */
export async function deleteNote(noteId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/notes/${encodeURIComponent(noteId)}`, {
        method: "DELETE"
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Delete note failed:", response.status, errorText);

        if (response.status === 404) {
            throw new Error("Note not found.");
        }
        throw new Error("Failed to delete note. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Pin/Unpin a note
 * PATCH /api/notes/{note_id}/pin
 * Body: { pinned: boolean }
 */
export async function toggleNotePin(noteId, pinned) {
    return updateNote(noteId, { pinned });
}

/**
 * Archive/Unarchive a note
 * PATCH /api/notes/{note_id}/archive
 * Body: { archived: boolean }
 */
export async function toggleNoteArchive(noteId, archived) {
    return updateNote(noteId, { archived });
}

/**
 * Fetch all unique tags used in notes
 * GET /api/notes/tags
 */
export async function fetchNoteTags() {
    const response = await fetchWithAuth(`${BASE_URL}/api/notes/tags`, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch tags failed:", response.status, errorText);
        throw new Error("Failed to fetch tags. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Add a reaction to a note
 * POST /api/notes/{note_id}/reactions
 * Body: { emoji: string }
 */
export async function addNoteReaction(noteId, emoji) {
    const response = await fetchWithAuth(`${BASE_URL}/api/notes/${encodeURIComponent(noteId)}/reactions`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            accept: "application/json"
        },
        body: JSON.stringify({ emoji })
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Add reaction failed:", response.status, errorText);
        throw new Error("Failed to add reaction. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Remove a reaction from a note
 * DELETE /api/notes/{note_id}/reactions/{reaction_id}
 */
export async function removeNoteReaction(noteId, reactionId) {
    const response = await fetchWithAuth(
        `${BASE_URL}/api/notes/${encodeURIComponent(noteId)}/reactions/${encodeURIComponent(reactionId)}`,
        { method: "DELETE" }
    );

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Remove reaction failed:", response.status, errorText);
        throw new Error("Failed to remove reaction. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Add a comment to a note
 * POST /api/notes/{note_id}/comments
 * Body: { content: string }
 */
export async function addNoteComment(noteId, content) {
    const response = await fetchWithAuth(`${BASE_URL}/api/notes/${encodeURIComponent(noteId)}/comments`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            accept: "application/json"
        },
        body: JSON.stringify({ content })
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Add comment failed:", response.status, errorText);
        throw new Error("Failed to add comment. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Fetch comments for a note
 * GET /api/notes/{note_id}/comments
 */
export async function fetchNoteComments(noteId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/notes/${encodeURIComponent(noteId)}/comments`, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch comments failed:", response.status, errorText);
        throw new Error("Failed to fetch comments. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Upload an attachment for a note
 * POST /api/notes/attachments
 * Body: FormData with file
 */
export async function uploadNoteAttachment(file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetchWithAuth(`${BASE_URL}/api/notes/attachments`, {
        method: "POST",
        body: formData
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Upload attachment failed:", response.status, errorText);
        throw new Error("Failed to upload attachment. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Fetch note statistics for the current user
 * GET /api/notes/stats
 */
export async function fetchNoteStats() {
    const response = await fetchWithAuth(`${BASE_URL}/api/notes/stats`, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch stats failed:", response.status, errorText);
        throw new Error("Failed to fetch statistics. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Search notes by content
 * GET /api/notes/search?q={query}
 */
export async function searchNotes(query, options = {}) {
    const params = new URLSearchParams({ q: query });
    if (options.limit) params.append('limit', options.limit);
    if (options.tag) params.append('tag', options.tag);

    const response = await fetchWithAuth(`${BASE_URL}/api/notes/search?${params}`, {
        headers: { accept: "application/json" }
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Search notes failed:", response.status, errorText);
        throw new Error("Failed to search notes. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Link a note to a case
 * POST /api/notes/{note_id}/link
 * Body: { case_id: string }
 */
export async function linkNoteToCase(noteId, caseId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/notes/${encodeURIComponent(noteId)}/link`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            accept: "application/json"
        },
        body: JSON.stringify({ case_id: caseId })
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Link note failed:", response.status, errorText);
        throw new Error("Failed to link note to case. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}

/**
 * Unlink a note from a case
 * DELETE /api/notes/{note_id}/link
 */
export async function unlinkNoteFromCase(noteId) {
    const response = await fetchWithAuth(`${BASE_URL}/api/notes/${encodeURIComponent(noteId)}/link`, {
        method: "DELETE"
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Unlink note failed:", response.status, errorText);
        throw new Error("Failed to unlink note from case. Please try again.");
    }

    const data = await response.json();
    return { status: response.status, data: data.data || data };
}
